import type { ItemRankSpread } from '../../hooks/useSessionResultsAnalytics';
import { C } from '../../theme/participantTokens';

interface ParticipantAlignmentScatterProps {
  spreads: ItemRankSpread[];
  participantRanksByItem: Record<string, Array<{ participantId: string; rank: number; isMe: boolean }>>;
  getItemLabel: (itemId: string) => string;
  maxItems?: number;
}

const CHART_W = 720;
const CHART_H = 120;
const COL_W = 120;

export function ParticipantAlignmentScatter({
  spreads,
  participantRanksByItem,
  getItemLabel,
  maxItems = 6,
}: ParticipantAlignmentScatterProps) {
  const items = spreads.slice(0, maxItems);
  if (items.length === 0) return null;

  const maxRank = Math.max(...items.flatMap(i => i.ranks), 1);
  const yScale = (rank: number) => 16 + ((rank - 1) / Math.max(maxRank - 1, 1)) * 68;

  return (
    <div
      className="pp-results-enter"
      style={{
        background: C.surface,
        border: `1px solid ${C.rule}`,
        borderLeft: `2px solid ${C.slate}`,
        borderRadius: 4,
        padding: '16px 18px',
        marginBottom: 14,
      }}
    >
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.15em', color: C.textTer, textTransform: 'uppercase', marginBottom: 6 }}>
        Participant Alignment — Score Spread
      </div>
      <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '1rem', fontWeight: 300, color: C.textSec, lineHeight: 1.5, marginBottom: 14 }}>
        Each dot is a participant&apos;s rank for an item. Tight clusters mean high consensus; spread signals divergence worth discussing.
      </div>
      <svg width="100%" height={CHART_H} viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="pp-dot-glow">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {[20, 55, 88].map(y => (
          <line key={y} x1={0} y1={y} x2={CHART_W} y2={y} stroke={C.rule} strokeWidth={0.5} />
        ))}
        {items.map((item, colIdx) => {
          const cx = 60 + colIdx * COL_W;
          const dots = participantRanksByItem[item.itemId] ?? [];
          const yMin = yScale(item.minRank);
          const yMax = yScale(item.maxRank);
          const bandColor = item.consensus === 'high' ? C.green : item.consensus === 'medium' ? C.slate : C.coral;
          const bandOpacity = item.consensus === 'high' ? 0.1 : item.consensus === 'medium' ? 0.08 : 0.07;

          return (
            <g key={item.itemId}>
              <rect
                x={cx - 20}
                y={Math.min(yMin, yMax) - 4}
                width={40}
                height={Math.abs(yMax - yMin) + 8}
                rx={2}
                fill={bandColor}
                opacity={bandOpacity}
              />
              {dots.map((d, di) => {
                const cy = yScale(d.rank) + (di % 3) * 0.8;
                const isMe = d.isMe;
                const fill = isMe
                  ? C.mustard
                  : item.consensus === 'high'
                    ? C.green
                    : item.consensus === 'low'
                      ? C.coral
                      : C.textTer;
                const opacity = isMe ? 0.95 : item.consensus === 'high' ? 0.65 : item.consensus === 'low' ? 0.45 : 0.4;
                return (
                  <circle
                    key={`${d.participantId}-${di}`}
                    cx={cx + (di % 5) * 3 - 6}
                    cy={cy}
                    r={isMe ? 3.5 : 3}
                    fill={fill}
                    opacity={opacity}
                    filter={isMe ? 'url(#pp-dot-glow)' : undefined}
                  />
                );
              })}
              <text
                x={cx}
                y={CHART_H - 2}
                textAnchor="middle"
                fontFamily="JetBrains Mono"
                fontSize={11}
                fill={C.textDim}
              >
                {getItemLabel(item.itemId).slice(0, 12)}
              </text>
            </g>
          );
        })}
        {/* Legend — left-anchored so "Diverge" can't overflow the right edge */}
        <circle cx={4} cy={8} r={4} fill={C.mustard} />
        <text x={12} y={13} fontFamily="JetBrains Mono" fontSize={11} fill={C.mustard}>You</text>
        <circle cx={52} cy={8} r={4} fill={C.green} opacity={0.6} />
        <text x={60} y={13} fontFamily="JetBrains Mono" fontSize={11} fill={C.textDim}>Consensus</text>
        <circle cx={140} cy={8} r={4} fill={C.coral} opacity={0.5} />
        <text x={148} y={13} fontFamily="JetBrains Mono" fontSize={11} fill={C.coral} opacity={0.8}>Divergence</text>
      </svg>
    </div>
  );
}
